import type { JSX } from "react";
import { lazy, Suspense, useCallback, useEffect, useState } from "react";

import { CityClock } from "./CityClock";
import { PlaneGlyph } from "./PlaneGlyph";
import { WeatherGlyph } from "./WeatherGlyph";

import type { LandingCity } from "@/lib/api";
import { readArrivals } from "@/lib/arrivals";
import { toAsciiUpper } from "@/lib/time";
import { readCurrentWeather } from "@/lib/weather";

interface CityIndexProps {
  cities: LandingCity[];
  /** GeoIP alpha-2 from /api/landing; forwarded to the city-request modal. */
  viewerCountry?: string | null;
  /** Real payload state. Loading and error are shown as such — never faked rows. */
  status: "loading" | "ready" | "error";
  /**
   * The instant the carrying document was rendered from (the payload's
   * `generatedAt`), or null when the rows are being rendered by a browser that
   * has no payload. The row clock's first render prints this instant so the home
   * document and the browser hydrating it agree (see CityClock).
   */
  renderedAt?: number | null;
  /**
   * Point the globe camera at a located city (preview only — never navigates).
   * The selection is sticky: the last hovered or focused city keeps the globe's camera, and
   * only another row replaces it. The globe's hover ink is *not* sticky — `null` releases it
   * (the routes fade out, the aircraft return to their resting grey) while the camera keeps
   * framing the city.
   */
  onHoverCity?: (name: string | null) => void;
}

interface VerifyPrefill {
  city: string;
  email: string;
  code: string;
}

/** Read ?verify-city=1&city&email&code (email one-click link) once, then strip it from the URL. */
function readVerifyPrefill(): VerifyPrefill | null {
  const params = new URLSearchParams(window.location.search);
  if (params.get("verify-city") !== "1") return null;
  const city = params.get("city")?.trim();
  const email = params.get("email")?.trim();
  const code = params.get("code")?.trim();
  window.history.replaceState({}, "", window.location.pathname + window.location.hash);
  return city && email && code && /^\d{6}$/.test(code) ? { city, email, code } : null;
}

const STRETCH_LINK = "atlas-row-link relative after:absolute after:inset-0 after:content-['']";

/**
 * The city-request popup, loaded the first time a reader opens it and never
 * before. It is the one part of the directory that needs nothing until a click:
 * its country dataset, flags and Turnstile widget are tens of KB that every
 * other visitor would pay for at first paint, and the dataset's locale loader
 * (`i18n-iso-countries`) resolves its JSON with a runtime `require` that cannot
 * be bundled into the worker's server renderer at all. So it is neither imported
 * at module scope nor mounted until `openRequest` runs, which also keeps the
 * server-rendered home document identical to the browser's first render — the
 * modal renders nothing in either until a reader asks for it.
 */
const CityRequestModal = lazy(
  // oxlint-disable-next-line noko-lint/no-inline-import -- deliberate code-split boundary: the popup is loaded on first open (see above)
  async () => ({ default: (await import("./CityRequestModal")).CityRequestModal }),
);

/**
 * One directory row. The destination element is the whole row (its ::after is
 * stretched over `.atlas-row` by home.css), so a tap on the name navigates in one
 * tap and there is exactly one interactive target per row. A located row also
 * previews the globe on hover or keyboard focus: the camera flight is sticky by
 * design, the hover ink is not — leaving the row releases the routes and the
 * accent aircraft (see CityIndex). Cities with no coordinates are still fully
 * navigable. A city that is announced but not live states the word in place of
 * its handle and is deliberately not a link: the row exists to send the reader
 * somewhere, and there is nowhere to send them yet.
 */
function CityRow({
  city,
  active,
  at,
  onSelect,
  onRelease,
}: {
  city: LandingCity;
  active: boolean;
  at?: number | null;
  onSelect: (name: string) => void;
  onRelease: () => void;
}): JSX.Element {
  const located = city.lat !== null && city.lng !== null;

  // The reading is whatever the feed left on the city, or nothing at all: an
  // absent, stale or unusable one leaves the row exactly as it was. Shared with
  // the guide header, which states the same degrees for the same city.
  const reading = readCurrentWeather(city.weather);
  // Today's arriving seats, the same reading the guide header states. A city
  // with nothing honest to show draws no figure at all.
  const arrivals = readArrivals(city.arrivals);
  // Announced, not live: no published guide and nothing collected on the city's
  // own Instagram account (see LandingCity.comingSoon).
  const comingSoon = city.comingSoon === true;

  const rowName = <span className="atlas-row-name">{toAsciiUpper(city.name)}</span>;
  const rowMeta = (
    <span className="atlas-row-meta">
      <span className="atlas-row-clock">
        <span className="atlas-row-clock-label">LOCAL TIME</span>{" "}
        <span className="atlas-row-clock-value">
          <CityClock timezone={city.timezone} className="tabular-nums" at={at} />
        </span>
      </span>
      {reading && (
        <span className="atlas-row-weather" aria-label={`${reading.degrees} degrees Celsius`}>
          {reading.glyph && <WeatherGlyph kind={reading.glyph.kind} night={reading.glyph.night} />}
          {reading.label}
        </span>
      )}
      {arrivals && (
        <span
          className="atlas-row-arrivals"
          aria-label={`${arrivals.label}. ${arrivals.hint}`}
          title={arrivals.hint}
        >
          <PlaneGlyph />
          {arrivals.label}
        </span>
      )}
      {comingSoon ? (
        <span className="atlas-row-soon">COMING SOON</span>
      ) : (
        city.instagramHandle && (
          <span className="atlas-row-handle">@{city.instagramHandle.toUpperCase()}</span>
        )
      )}
    </span>
  );

  return (
    <div
      className={active ? "atlas-row is-active" : "atlas-row"}
      onMouseEnter={located ? () => onSelect(city.name) : undefined}
      onFocus={located ? () => onSelect(city.name) : undefined}
      // A mouse leaves this row for the globe — that is where the city's routes and airports are,
      // and they have to stay lit while the pointer crosses to them — so the ink is not released
      // here. Blur still releases it: a keyboard reader has no pointer for the globe's own release
      // to answer, and `MapSection` owns the lifetime the mouse does have.
      onBlur={located ? onRelease : undefined}
    >
      {city.cityPagePublished && city.slug ? (
        // A published guide is a prerendered document — its body and its payload
        // are the same file — so the destination is a link, not a client route:
        // as an in-app navigation the SPA keeps running the directory's document,
        // finds no guide payload in it and renders the placeholder instead.
        <a className={STRETCH_LINK} href={`/page/${city.slug}`}>
          {rowName}
          {rowMeta}
        </a>
      ) : !comingSoon && city.instagramHandle ? (
        <a
          className={STRETCH_LINK}
          href={`https://instagram.com/${city.instagramHandle}`}
          target="_blank"
          rel="noreferrer"
        >
          {rowName}
          {rowMeta}
        </a>
      ) : (
        <span className="atlas-row-link">
          {rowName}
          {rowMeta}
        </span>
      )}
    </div>
  );
}

/**
 * The city directory beat. It is the third scroll beat of the atlas scene (App
 * places it inside `.atlas-scene`, which MapSection measures), the `#cities`
 * anchor target, and the owner of the city-request flow — all three exist in
 * every state, including a failed or unpublished payload.
 */
export function CityIndex({
  cities,
  viewerCountry,
  status,
  renderedAt,
  onHoverCity,
}: CityIndexProps): JSX.Element {
  const [requestOpen, setRequestOpen] = useState(false);
  // The modal itself is code-split and mounts no earlier than the first open (see
  // CityRequestModal above): this flag latches, so closing the popup keeps it
  // mounted for its close transition but a reader who never opens it never pays
  // for it. It stays false through the server render too.
  const [requestMounted, setRequestMounted] = useState(false);
  const openRequest = useCallback((): void => {
    setRequestMounted(true);
    setRequestOpen(true);
  }, []);
  const [prefill, setPrefill] = useState<VerifyPrefill | null>(null);
  const [selected, setSelected] = useState<string | null>(null);

  // The selection is sticky: the last hovered or focused city keeps the globe's camera, and only
  // selecting another row replaces it. The globe's ink follows that selection for as long as the
  // reader stays on this chapter with a pointer in the page — `MapSection` owns that lifetime,
  // because the ink has to stay lit while the pointer crosses from this row to the airports and the
  // aircraft it lights. Blur releases it for the keyboard, which has no pointer for the globe's own
  // release to answer. `selected` deliberately survives the release: the row's `is-active` mirrors
  // the marker's lit state, which is the selection, and clearing one without the other would be a
  // row-vs-marker disagreement.
  const select = useCallback(
    (name: string): void => {
      setSelected(name);
      onHoverCity?.(name);
    },
    [onHoverCity],
  );

  const release = useCallback((): void => {
    onHoverCity?.(null);
  }, [onHoverCity]);

  // Email one-click link: open the confirm dialog prefilled.
  useEffect(() => {
    const link = readVerifyPrefill();
    if (link) {
      setPrefill(link);
      openRequest();
    }
  }, [openRequest]);

  return (
    <section id="cities" className="atlas-beat atlas-directory" data-atlas-beat="cities">
      <div className="atlas-directory-head">
        <p className="atlas-kicker">03 / FIND YOUR CITY</p>
        <h2 className="atlas-display">
          <span className="atlas-display-line">WHERE ARE</span>
          <span className="atlas-display-line">YOU GOING?</span>
        </h2>
      </div>

      {/* The selection is sticky, and so is the ink while the reader is on this chapter: a row's
          blur releases the globe's routes and accent aircraft (`release`), and for a pointer the
          globe releases them itself when the pointer leaves the page or the chapter. */}
      <div className="atlas-rows">
        {status === "loading" && <output className="atlas-state">LOADING CITIES…</output>}
        {status === "error" && (
          <div className="atlas-state">
            <p className="atlas-state-text">{"COULDN'T LOAD THE CITIES."}</p>
            <button
              type="button"
              className="atlas-state-action"
              onClick={() => window.location.reload()}
            >
              RELOAD
            </button>
          </div>
        )}
        {status === "ready" && cities.length === 0 && (
          <p className="atlas-state">NO CITIES TO SHOW YET.</p>
        )}
        {cities.map((city, i) => (
          <CityRow
            key={`${city.name}:${i}`}
            city={city}
            active={selected === city.name}
            at={renderedAt}
            onSelect={select}
            onRelease={release}
          />
        ))}
      </div>

      <div className="atlas-request">
        <button type="button" className="atlas-request-button" onClick={openRequest}>
          WANT YOUR CITY ADDED? →
        </button>
      </div>

      {requestMounted && (
        <Suspense fallback={null}>
          <CityRequestModal
            open={requestOpen}
            onClose={() => setRequestOpen(false)}
            prefill={prefill}
            viewerCountry={viewerCountry}
          />
        </Suspense>
      )}
    </section>
  );
}
